import { Button, CloseButton, Drawer, Flex } from '@chakra-ui/react';
import { useAddCustomField, useDeleteCustomField, useDrawer, useGetSingleCustomField, useUpdateCustomField } from '@admin';
import { ConfirmModal, FormInput, Loading, toaster, type ConfirmModalHandle } from '@shared';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

type CustomFieldForm = {
	label: string;
};

export default function CustomFieldDrawer({ customFieldID, onCreated }: { customFieldID?: number; onCreated?: (customField: CustomField) => void }) {
	const { t } = useTranslation();
	const { mutate: updateCustomField, isPending: isUpdatingCustomField } = useUpdateCustomField();
	const { mutate: createCustomField, isPending: isCreatingCustomField } = useAddCustomField();

	const { closeDrawer } = useDrawer();
	const queryClient = useQueryClient();

	const { data: customField, isFetching: isFetchingCustomField } = useGetSingleCustomField(customFieldID as number, {
		isEnabled: !!customFieldID,
	});

	const methods = useForm<CustomFieldForm>({
		defaultValues: {
			...customField,
		},
	});

	const {
		handleSubmit,
		reset,
		control,
		formState: { dirtyFields, isDirty },
	} = methods;

	useEffect(() => {
		if (customField) {
			reset({
				label: customField.label,
			});
		}
	}, [customField, reset]);

	function saveCustomField(values: CustomFieldForm): void {
		if (customFieldID) {
			const payload: Partial<UpdateCustomFieldRequest> = {
				customFieldID: customFieldID,
			};

			(Object.keys(dirtyFields) as Array<keyof CustomFieldForm>).forEach(key => {
				const value = values[key];

				if (value !== undefined) {
					payload[key] = value;
				}
			});

			updateCustomField(payload, {
				onSuccess: async data => {
					toaster.create({
						title: t('general.saved'),
						type: 'success',
					});
					queryClient.refetchQueries({ queryKey: ['customFields'] });
					queryClient.setQueryData(['customField', data.customFieldID], data);

					closeDrawer('customFieldDrawer');
				},
			});
		} else {
			createCustomField(
				{ label: values.label },
				{
					onSuccess: async customField => {
						toaster.create({
							title: t('general.saved'),
							type: 'success',
						});
						queryClient.refetchQueries({ queryKey: ['customFields'] });
						closeDrawer('customFieldDrawer');
						if (onCreated) {
							onCreated(customField);
						}
					},
				},
			);
		}
	}

	const confirmRemoveRef = useRef<ConfirmModalHandle>(null);
	const { mutate: deleteCustomField } = useDeleteCustomField();

	async function handleRemoveClick() {
		const response = await confirmRemoveRef.current?.open({
			title: t('general.remove_custom_field'),
			content: t('general.really_remove_custom_field'),
			confirmButtonText: t('general.remove'),
			confirmButtonVariant: 'subtle',
		});

		if (!customFieldID) {
			return;
		}

		if (response === true) {
			deleteCustomField(customFieldID, {
				onSuccess: () => {
					toaster.create({
						type: 'success',
						title: t('general.removed'),
					});
					queryClient.invalidateQueries({ queryKey: ['customFields'] });
					closeDrawer('customFieldDrawer');
				},
			});
		}
	}

	const label = useWatch({ control, name: 'label' });

	return isFetchingCustomField ? (
		<Loading />
	) : (
		<FormProvider {...methods}>
			<form style={{ height: '100%', display: 'flex', flexDirection: 'column' }} onSubmit={handleSubmit(saveCustomField)}>
				<Drawer.Header>
					<Drawer.Title marginRight="20px" wordBreak={'break-word'}>
						{customFieldID ? label : t('general.add_custom_field')}
					</Drawer.Title>
				</Drawer.Header>
				<Drawer.Body flex={1}>
					<Flex flexDirection={'column'} gap={4} marginBottom={3} width={'100%'}>
						<FormInput label={t('general.label')} isRequired={true} name="label" maxLength={100} />
					</Flex>
				</Drawer.Body>
				<Drawer.Footer>
					<ConfirmModal ref={confirmRemoveRef} />

					{customFieldID ? (
						<Button data-cy="removeCustomFieldButton" marginRight={'auto'} onClick={handleRemoveClick} variant="subtle">
							{t('general.remove')}
						</Button>
					) : null}
					<Button data-cy="customFieldDrawerCancelButton" onClick={() => closeDrawer('customFieldDrawer')} variant="outline">
						{t('general.cancel')}
					</Button>
					<Button type="submit" disabled={isUpdatingCustomField || !isDirty || isCreatingCustomField}>
						{isUpdatingCustomField ? t('general.please_wait') : t('general.save')}
					</Button>
				</Drawer.Footer>

				<Drawer.CloseTrigger asChild>
					<CloseButton size="sm" />
				</Drawer.CloseTrigger>
			</form>
		</FormProvider>
	);
}
